import moment, {locale} from 'moment'
import React from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import { Paragraph } from '../../../common/styled-components/typography'
import { useDateFormats } from '../../../hooks/useDateFormats'
import { getDisplayableAgeRestriction, hasGroupPlacesLimited } from '../../../utils/qidigo'
import { getHtmlContent } from '../../../utils/text'
import { ICalendarGroup, ICalendarSession } from '../../calendar/types'
import GenderRestrictions from "../../GenderRestrictions";

const GroupInformationContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
`

const InfoBlockContainer = styled.div`
  flex-shrink: 0;
  margin-right: 10px;
  width: 120px;
  min-width: 100px;
  max-width: 20%;
  font-weight: bold;
`

const CompletedGroup = styled.span`
  color: red;
  text-transform: capitalize;
`

const StyledParagraph = styled(Paragraph)`
  text-align: justify;
  font-size: inherit;
`

const makeSessionSpaceComponent = (group: ICalendarGroup, hasDropInOffer: boolean|undefined, t: any) => {
  let quantityText = <>{group.nbr_places_remaining}</>;
  if (group.nbr_places_remaining === null || group.nbr_places_remaining > 5) {
    quantityText = <>{"5+"}</>;
  } else if (group.nbr_places_remaining <= 0) {
    quantityText = <CompletedGroup>{t("offers.noCapacity")}</CompletedGroup>;
  }

  return (
    <div>
      { hasDropInOffer ? t("offers.sessionAvailabillity") : t("offers.placesRemaining") }
      {quantityText}
    </div>
  );
}

const makeDropInSpaceComponent = (sessions: ICalendarSession[], hasSessionOffer: boolean|undefined, t: any) => {
  const dropInCapacities = sessions.map((session) => {
    if (!!session.end_date && moment(session.end_date).isBefore(moment())) {
        return 0;
    }

    if (session.registration_limit !== null && session.nbr_places_remaining !== null) {
      return session.registration_limit < session.nbr_places_remaining
        ? session.registration_limit
        : session.nbr_places_remaining;
    } else if (session.registration_limit !== null) {
      return session.registration_limit;
    } else if (session.nbr_places_remaining !== null) {
      return session.nbr_places_remaining;
    }

    return null;
  });

  let totalCapacity: number|null = 0;
  dropInCapacities.some(
    (capacity) => {
      if (totalCapacity === null) {
          return true;
      }

      if (capacity === null) {
          totalCapacity = null;

          return true;
      }

      totalCapacity += capacity;

      return totalCapacity > 5;
    }
  );

  let quantityText = <>{totalCapacity}</>;
  if (totalCapacity === null || totalCapacity > 5) {
    quantityText = <>{"5+"}</>;
  } else if (totalCapacity <= 0) {
    quantityText = <CompletedGroup>{t("offers.noCapacity")}</CompletedGroup>;
  }

  return (
    <div>
      { hasSessionOffer ? t("offers.dropInAvailabillity") : t("offers.placesRemaining") }
      {quantityText}
    </div>
  );
}

interface GroupInformationCollapseViewProps {
  group: ICalendarGroup;
  withLocation?: boolean;
  withGroupDescription?: boolean;
  hasSessionOffer?: boolean;
  hasDropInOffer?: boolean;
}

const GroupInformationCollapseView: React.FC<GroupInformationCollapseViewProps> = ({ group, withLocation, withGroupDescription, hasSessionOffer, hasDropInOffer }) => {
  const { t } = useTranslation();
  const { shortFormat } = useDateFormats()

  const displayableDuration = group.start_date && (group.end_date
    ? t(
      'from$startDateTo$endDate',
      {
        startDate: moment(group.start_date).format(shortFormat),
        endDate: moment(group.end_date).format(shortFormat)
      }
    )
    : t('on$date', { date: moment(group.start_date).format(shortFormat) })
  );

  let sessionSpace = hasSessionOffer ? makeSessionSpaceComponent(group, hasDropInOffer, t) : null;
  let dropInSpace = hasDropInOffer ? makeDropInSpaceComponent(group.sessions, hasSessionOffer, t) : null;

  return (
    <>
      {
        withGroupDescription && (
          <StyledParagraph dangerouslySetInnerHTML={{ __html: getHtmlContent(group.description) }} />
        )
      }
      <GroupInformationContainer>
        <InfoBlockContainer>
          {t('scheduleShort')}
        </InfoBlockContainer>
        <div className={"schedule"}>
          {
            (withLocation && !!group.location) &&
            <div>
              {group.location.full_name}
            </div>
          }
          {
            displayableDuration
          }
          {
            group.schedules && group.schedules.map((x, i) =>
              <div key={`${x.type_day}-${x.start_time}-${x.end_time}-${i}`}>
                {t(x.type_day.toLowerCase())}
                {' '}
                {moment(x.start_time).format(locale() === 'en-ca' ? 'h:mm a' : 'HH[h]mm')}
                {' - '}
                {moment(x.end_time).format(locale() === 'en-ca' ? 'h:mm a' : 'HH[h]mm')}
              </div>
            )
          }
        </div>
      </GroupInformationContainer>
      {
        (hasGroupPlacesLimited(group)
          || group.restrictions_gender
          || group.restriction_age_min
          || group.restriction_age_max
        ) &&
        <GroupInformationContainer>
          <InfoBlockContainer>
            {t('restrictions.title')}
          </InfoBlockContainer>
          <div className={"restrictions"}>
            {sessionSpace}
            {dropInSpace}
            {
              group.restrictions_gender &&
              <div className={'inline'}>
                <b>{t('gender') + t('column')}</b>
                <GenderRestrictions
                    genders={group.restrictions_gender}
                    translation={t}
                    extraInfoMessage={""}
                    forPassModal={true}
                />
              </div>
            }
            {
              (group.restriction_age_min || group.restriction_age_max) &&
              <div>
                <b>{t('age') + t('column')}</b>{getDisplayableAgeRestriction(t, group.restriction_age_min, group.restriction_age_max)}
              </div>
            }
          </div>
        </GroupInformationContainer>
      }
    </>
  )
};

export default GroupInformationCollapseView;
